import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import PlateformService from '../../components/PlateformService';
import CategorySyncModal from '../../components/CategorySyncModal';
import ProductSyncModal from '../../components/ProductSyncModal';
import GlovoServiceManager from '../../components/GlovoServiceManager';
import {
  // Authentication
  getTokens,
  testConnection,
  loginOrCreate,
  // Orders
  syncOrders,
  syncOrdersHistory,
  // Products
  syncProducts,
  toggleProduct,
  createProduct,
  resolveProductConflict,
  syncProductsByCategory,
  getProductsByCategory,
  // Categories
  syncCategories,
  syncCategoriesPortal,
  // Reviews
  syncReviews,
  fetchAllReviews,
  // Addons
  syncAddons,
  matchAddons,
  // Partner
  getPartnerStoreContent,
  // Promotions
  syncPromotions,
  // Legacy
  fetchGlovoAvailabilities,
  fetchGlovoReviews,
  fetchGlovoStatus,
} from '../../components/GlobalGlovoService';
import GlovoModal from '../../components/GlovoModal';
import useGlovoModal from '../../components/useGlovoModal';

const statusBadgeClass = (isConnected) =>
  `badge rounded-pill ${isConnected ? 'bg-success' : 'bg-secondary'}`;

const formatAvailabilityLabel = (availabilityState) => {
  if (!availabilityState) return 'Inconnue';
  return availabilityState
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

function GlovoPlatformDashboard() {
  const [loadingPlateforms, setLoadingPlateforms] = useState(false);
  const [plateforms, setPlateforms] = useState([]);
  const [selectedPlateformId, setSelectedPlateformId] = useState('');
  const [loginStatus, setLoginStatus] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilities, setAvailabilities] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusPayload, setStatusPayload] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsPayload, setReviewsPayload] = useState(null);
  const [error, setError] = useState('');
  
  // Reviews section state
  const [reviewsData, setReviewsData] = useState([]);
  const [reviewsFromDate, setReviewsFromDate] = useState('');
  const [reviewsToDate, setReviewsToDate] = useState('');
  const [selectedStoresForReviews, setSelectedStoresForReviews] = useState([]);
  const [reviewsFetching, setReviewsFetching] = useState(false);
  const [reviewsStats, setReviewsStats] = useState(null);
  const [allStores, setAllStores] = useState([]);
  
  // Category sync modal state
  const [categorySyncLoading, setCategorySyncLoading] = useState(false);
  const [categorySyncData, setCategorySyncData] = useState(null);
  const [categorySyncError, setCategorySyncError] = useState(null);
  const [selectedStoreForCategorySync, setSelectedStoreForCategorySync] = useState(null);
  
  // Product sync modal state
  const [productSyncLoading, setProductSyncLoading] = useState(false);
  const [productSyncData, setProductSyncData] = useState(null);
  const [productSyncError, setProductSyncError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Promotions sync state
  const [promotionsSyncing, setPromotionsSyncing] = useState(false);
  const [promotionsResult, setPromotionsResult] = useState(null);
  const [promotionsError, setPromotionsError] = useState(null);
  const [selectedStoresForPromotions, setSelectedStoresForPromotions] = useState([]);
  const [promotionsStartDate, setPromotionsStartDate] = useState('');
  
  // Orders sync state
  const [ordersSyncLoading, setOrdersSyncLoading] = useState(false);
  const [ordersSyncResult, setOrdersSyncResult] = useState(null);
  const [ordersSyncError, setOrdersSyncError] = useState(null);
  const [selectedStoreForOrders, setSelectedStoreForOrders] = useState(null);
  const [ordersTimeFrom, setOrdersTimeFrom] = useState('');
  const [ordersTimeTo, setOrdersTimeTo] = useState('');
  const [isAutoSyncEnabled, setIsAutoSyncEnabled] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  // Sync User Data state
  const [syncUserLimit, setSyncUserLimit] = useState(50);
  const [syncUserLoading, setSyncUserLoading] = useState(false);
  const [syncUserResult, setSyncUserResult] = useState(null);
  const [syncUserError, setSyncUserError] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Use Glovo Modal Hook
  const {
    modalData,
    modalLoading,
    modalError,
    modalTitle,
    closeModal,
    executeWithModal,
  } = useGlovoModal();

  const glovoPlateforms = useMemo(
    () =>
      plateforms.filter(
        (plateform) =>
          (plateform?.type || '').toLowerCase() === 'glovo' &&
          plateform?.additionnal_data?.isportal === true
      ),
    [plateforms]
  );

  const selectedPlateform = useMemo(
    () => glovoPlateforms.find((plateform) => (plateform?._id || plateform?.id) === selectedPlateformId),
    [glovoPlateforms, selectedPlateformId]
  );

  const hasPortalSession = useMemo(() => {
    if (!selectedPlateform || !selectedPlateform.additionnal_data) return false;
    const { additionnal_data: data } = selectedPlateform;
    return Boolean(
      data.portal?.keymaker?.access_token ||
        data.portal?.refresh_token ||
        data.glovo_portal_bearer ||
        data.portal_access_token
    );
  }, [selectedPlateform]);

  const glovoOptions = useMemo(
    () =>
      glovoPlateforms.map((plateform) => {
        const value = plateform._id || plateform.id;
        return {
          value,
          label: plateform.name || plateform.login || value,
        };
      }),
    [glovoPlateforms]
  );

  const loadPlateforms = async () => {
    try {
      setLoadingPlateforms(true);
      const data = await PlateformService.fetchPlateforms();
      const list = data.response || data;
      setPlateforms(Array.isArray(list) ? list : []);
    } catch (fetchError) {
      console.error(fetchError);
      setError(
        "Impossible de charger les plateformes Glovo. Vérifiez votre connexion ou vos permissions."
      );
    } finally {
      setLoadingPlateforms(false);
    }
  };

  useEffect(() => {
    loadPlateforms();
    
    // Initialize dates (last 7 days)
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    setReviewsToDate(today.toISOString().split('T')[0]);
    setReviewsFromDate(weekAgo.toISOString().split('T')[0]);
  }, []);

  // Load stores when platform changes
  useEffect(() => {
    const loadStores = async () => {
      if (!selectedPlateform || !selectedPlateform.store_ids || selectedPlateform.store_ids.length === 0) {
        setAllStores([]);
        setSelectedStoresForReviews([]);
        return;
      }

      try {
        const axios = (await import('axios')).default;
        const { header } = await import('../../components/header.encry');
        const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
        const response = await axios.get(`${API_URL}/stores`, header());
        const stores = response.data.response || response.data;
        
        const platformStores = stores.filter(store => 
          selectedPlateform.store_ids.some(id => 
            (id.$oid || id) === (store._id || store.id)
          )
        );
        
        setAllStores(platformStores);
      } catch (err) {
        console.error('Error loading stores:', err);
      }
    };

    loadStores();
  }, [selectedPlateform]);

  useEffect(() => {
    if (glovoOptions.length > 0 && !selectedPlateformId) {
      setSelectedPlateformId(glovoOptions[0].value);
    }
  }, [glovoOptions, selectedPlateformId]);

  // Auto-sync orders every 5 minutes
  useEffect(() => {
    if (!isAutoSyncEnabled || !selectedPlateformId) return;

    const syncInterval = setInterval(async () => {
      try {
        const payload = {
          plateformId: selectedPlateformId,
          globalEntityId: 'GV_MA',
        };
        
        if (selectedStoreForOrders?.storeCode) {
          payload.vendorId = selectedStoreForOrders.storeCode;
        }
        
        if (ordersTimeFrom) {
          payload.timeFrom = new Date(ordersTimeFrom).toISOString();
        }
        
        if (ordersTimeTo) {
          payload.timeTo = new Date(ordersTimeTo).toISOString();
        }
        
        const response = await syncOrders(payload);
        setOrdersSyncResult(response);
        setLastSyncTime(new Date());
      } catch (err) {
        console.error('Auto-sync error:', err);
        setOrdersSyncError(err?.response?.data?.error || err.message);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(syncInterval);
  }, [isAutoSyncEnabled, selectedPlateformId, selectedStoreForOrders, ordersTimeFrom, ordersTimeTo]);

  const handleSelectPlateform = (option) => {
    setSelectedPlateformId(option?.value || '');
    setLoginStatus(null);
    setAvailabilities([]);
    setStatusPayload(null);
    setReviewsPayload(null);
    setError('');
  };

  const handleLogin = async () => {
    if (!selectedPlateformId) return;
    try {
      setLoginLoading(true);
      setError('');
      const response = await getTokens({ 
        plateformId: selectedPlateformId,
        forceLogin: true 
      });
      setLoginStatus({
        source: response?.tokens?.source || 'login',
        plateformId: response?.plateformId || selectedPlateformId,
      });
      await loadPlateforms();
    } catch (loginError) {
      console.error(loginError);
      const message =
        loginError?.response?.data?.error ||
        loginError?.response?.data?.message ||
        loginError.message ||
        'Échec de la connexion au portail Glovo.';
      setError(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleFetchAvailabilities = async () => {
    if (!selectedPlateformId) return;
    try {
      setAvailabilityLoading(true);
      setError('');
      const response = await fetchGlovoAvailabilities({ plateformId: selectedPlateformId });
      setAvailabilities(response?.availabilities || []);
      setLoginStatus((prev) => ({
        ...prev,
        source: response?.source || prev?.source,
      }));
    } catch (availabilityError) {
      console.error(availabilityError);
      const message =
        availabilityError?.response?.data?.error ||
        availabilityError?.response?.data?.message ||
        availabilityError.message ||
        'Impossible de récupérer les disponibilités Glovo.';
      setError(message);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleFetchStatus = async () => {
    if (!selectedPlateformId) return;
    try {
      setStatusLoading(true);
      setError('');
      const response = await fetchGlovoStatus({ plateformId: selectedPlateformId });
      setStatusPayload(response?.status || response);
      setLoginStatus((prev) => ({
        ...prev,
        source: response?.source || prev?.source,
      }));
    } catch (statusError) {
      console.error(statusError);
      const message =
        statusError?.response?.data?.error ||
        statusError?.response?.data?.message ||
        statusError.message ||
        'Impossible de récupérer le statut Glovo.';
      setError(message);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleFetchReviews = async () => {
    if (!selectedPlateformId) return;
    try {
      setReviewsLoading(true);
      setError('');
      const response = await fetchGlovoReviews({ plateformId: selectedPlateformId });
      setReviewsPayload(response?.reviews || response);
      setLoginStatus((prev) => ({
        ...prev,
        source: response?.source || prev?.source,
      }));
    } catch (reviewsError) {
      console.error(reviewsError);
      const message =
        reviewsError?.response?.data?.error ||
        reviewsError?.response?.data?.message ||
        reviewsError.message ||
        'Impossible de récupérer les avis Glovo.';
      setError(message);
    } finally {
      setReviewsLoading(false);
    }
  };

  // New modal handlers using the hook
  const handleSyncReviews = async () => {
    if (!selectedPlateformId) return;
    
    // Calculate date range (last 7 days by default)
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 7);
    
    executeWithModal(
      'Récupération des Avis Glovo (7 derniers jours)',
      () => fetchAllReviews({ 
        plateformId: selectedPlateformId,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        perPage: 50,
        hasText: false
      })
    );
  };

  // Handler for dedicated reviews section
  const handleFetchReviewsSection = async () => {
    if (!selectedPlateformId) return;
    
    setReviewsFetching(true);
    setError('');
    
    try {
      const globalVendorCodes = selectedStoresForReviews.length > 0
        ? selectedStoresForReviews.map(s => s.storeCode).filter(Boolean)
        : undefined;

      const fromDateISO = reviewsFromDate ? new Date(reviewsFromDate).toISOString() : undefined;
      const toDateISO = reviewsToDate ? new Date(reviewsToDate + 'T23:59:59').toISOString() : undefined;

      const result = await fetchAllReviews({
        plateformId: selectedPlateformId,
        globalVendorCodes,
        fromDate: fromDateISO,
        toDate: toDateISO,
        perPage: 50,
        hasText: false
      });

      setReviewsData(result.reviews || []);
      
      // Calculate stats
      if (result.reviews && result.reviews.length > 0) {
        const ratings = result.reviews.map(r => r.rating || 0);
        const avgRating = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        const withText = result.reviews.filter(r => r.text && r.text.trim()).length;

        setReviewsStats({
          total: result.total,
          pages: result.pages,
          avgRating: avgRating.toFixed(2),
          withText,
          fiveStars: result.reviews.filter(r => r.rating === 5).length,
          fourStars: result.reviews.filter(r => r.rating === 4).length,
          threeStars: result.reviews.filter(r => r.rating === 3).length,
          twoStars: result.reviews.filter(r => r.rating === 2).length,
          oneStar: result.reviews.filter(r => r.rating === 1).length
        });
      } else {
        setReviewsStats(null);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err?.response?.data?.error || err.message || 'Erreur lors de la récupération des avis');
    } finally {
      setReviewsFetching(false);
    }
  };

  const handleSyncCategories = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Catégories',
      () => syncCategories({ plateformId: selectedPlateformId })
    );
  };

  const handleSyncCategoriesPortal = async () => {
    if (!selectedPlateformId || !selectedPlateform) return;
    
    // Check if a store is selected
    if (!selectedStoreForCategorySync) {
      setError('Veuillez sélectionner un magasin pour synchroniser les catégories');
      return;
    }

    // Find the actual store object from allStores to get the storeCode
    const selectedStore = allStores.find(store => 
      (store._id || store.id) === selectedStoreForCategorySync.value
    );

    console.log('Debug - Selected store object:', selectedStore);
    console.log('Debug - selectedStoreForCategorySync:', selectedStoreForCategorySync);

    if (!selectedStore) {
      setError('Magasin introuvable');
      return;
    }

    // Use the storeCode as vendorId (e.g., "GV_MA;149345")
    // Note: payload.vendorId = storeCode
    const vendorId = selectedStore.storeCode;
    if (!vendorId) {
      setError('Store Code non trouvé dans le magasin sélectionné');
      return;
    }

    console.log('Syncing categories with:', {
      plateformId: selectedPlateformId,
      vendorId: vendorId,
      storeId: selectedStoreForCategorySync.value,
      storeName: selectedStore.name,
      fullStoreObject: selectedStore
    });

    setCategorySyncLoading(true);
    setCategorySyncError(null);
    setCategorySyncData(null);
    setError('');

    // Open modal with Bootstrap (globally available)
    const modalElement = document.getElementById('categorySyncModal');
    if (modalElement && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalElement);
      modal.show();
    }

    try {
      const result = await syncCategoriesPortal({
        plateformId: selectedPlateformId,
        vendorId: vendorId, // storeCode (e.g., "GV_MA;149345")
        globalEntityId: 'GV_MA',
        storeId: selectedStoreForCategorySync.value
      });

      console.log('Category sync result:', result);
      
      // Ensure we have the categories array
      const categoriesData = {
        ...result,
        categories: result.categories || []
      };
      
      setCategorySyncData(categoriesData);
    } catch (err) {
      console.error('Error syncing categories:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Erreur lors de la synchronisation des catégories';
      setCategorySyncError(errorMessage);
    } finally {
      setCategorySyncLoading(false);
    }
  };

  const handleCategoryClick = async (category) => {
    if (!selectedPlateformId || !selectedPlateform || !category) return;
    
    const vendorId = selectedPlateform.additionnal_data?.vendor_id || selectedPlateform.additionnal_data?.vendorId;
    if (!vendorId) {
      setError('Vendor ID non trouvé dans la plateforme sélectionnée');
      return;
    }

    setSelectedCategory(category);
    setProductSyncLoading(true);
    setProductSyncError(null);
    setProductSyncData(null);

    // Open product modal
    const modalElement = document.getElementById('productSyncModal');
    if (modalElement && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalElement);
      modal.show();
    }

    try {
      // First get the products from the category
      const productsResult = await getProductsByCategory({
        plateformId: selectedPlateformId,
        vendorId: vendorId,
        catalogId: category.catalog_id || category.catalogId,
        categoryId: category.remote_plateform_id,
        globalEntityId: 'GV_MA'
      });

      // Then sync the products
      const syncResult = await syncProductsByCategory({
        plateformId: selectedPlateformId,
        vendorId: vendorId,
        categoryId: category.remote_plateform_id,
        globalEntityId: 'GV_MA',
        storeCode: null
      });

      setProductSyncData({
        ...syncResult,
        products: productsResult.products || []
      });
    } catch (err) {
      console.error('Error syncing products:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Erreur lors de la synchronisation des produits';
      setProductSyncError(errorMessage);
    } finally {
      setProductSyncLoading(false);
    }
  };

  const handleSyncProducts = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Produits',
      () => syncProducts({ plateformId: selectedPlateformId })
    );
  };

  const handleSyncOrders = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Commandes (4 derniers jours)',
      () => syncOrders({ 
        plateformId: selectedPlateformId,
        days: 4
      })
    );
  };

  const handleSyncOrdersHistory = () => {
    if (!selectedPlateformId) return;
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7); // Last 7 days
    
    executeWithModal(
      'Synchronisation Historique des Commandes',
      () => syncOrdersHistory({ 
        plateformId: selectedPlateformId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      })
    );
  };

  const handleSyncAddons = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Addons',
      () => syncAddons({ plateformId: selectedPlateformId })
    );
  };

  const handleTestConnection = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Test de Connexion Glovo',
      () => testConnection({ plateformId: selectedPlateformId })
    );
  };

  const handleSyncUserData = async (overridePlateformId) => {
    // limit is adjustable up to 50 (UI enforces), default 50
    const limitNum = Math.max(1, Math.min(50, Number(syncUserLimit) || 50));
    setSyncUserLoading(true);
    setSyncUserError(null);
    setSyncUserResult(null);

    try {
      const axios = (await import('axios')).default;
      const { header } = await import('../../components/header.encry');
      const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

      // Choose platform: override param -> selected -> first portal-enabled glovo platform
      const platformToUse = overridePlateformId || selectedPlateformId || (glovoPlateforms[0]?._id || glovoPlateforms[0]?.id);
      if (!platformToUse) {
        setSyncUserError('Aucune plateforme Glovo (portal-enabled) trouvée');
        setSyncUserLoading(false);
        return;
      }

      const payload = { limit: limitNum, plateformId: platformToUse };

      const response = await axios.post(`${API_URL}/glovo-portal/sync-user-data`, payload, header());
      // Try to extract a JSON-serializable payload
      const raw = response.data?.response ?? response.data ?? response;
      let safe = raw;
      try {
        // Attempt deep-serialize to strip circular refs
        safe = JSON.parse(JSON.stringify(raw));
      } catch (e) {
        // Fallback: keep only known scalar/top-level fields if present
        safe = {};
        if (raw && typeof raw === 'object') {
          if (raw.processed != null) safe.processed = raw.processed;
          if (raw.updated != null) safe.updated = raw.updated;
          if (raw.failed != null) safe.failed = raw.failed;
          if (Array.isArray(raw.details)) safe.details = raw.details.map(d => {
            try {
              return JSON.parse(JSON.stringify(d));
            } catch (_) {
              // shallow copy useful fields
              return {
                orderId: d?.orderId,
                orderCode: d?.orderCode,
                ok: d?.ok,
                status: d?.status,
                reason: d?.reason,
                error: d?.error
              };
            }
          });
        } else {
          safe.raw = String(raw);
        }
      }

      setSyncUserResult(safe);
    } catch (err) {
      console.error('Error syncing user data:', err);
      setSyncUserError(err?.response?.data?.error || err.message || 'Erreur lors de la synchronisation des utilisateurs');
    } finally {
      setSyncUserLoading(false);
    }
  };

  

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center mb-4">
        <div>
          <h3 className="mb-1">Plateforme Glovo</h3>
          <p className="mb-0 text-muted">
            Gérer la connexion au portail Glovo et consulter la disponibilité des restaurants.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="flex-grow-1" style={{ minWidth: '260px' }}>
              <label className="form-label">Plateforme Glovo</label>
              <Select
                options={glovoOptions}
                value={glovoOptions.find((option) => option.value === selectedPlateformId) || null}
                onChange={handleSelectPlateform}
                isLoading={loadingPlateforms}
                placeholder="Sélectionnez une plateforme Glovo"
                noOptionsMessage={() =>
                  loadingPlateforms
                    ? 'Chargement des plateformes…'
                    : 'Aucune plateforme Glovo disponible'
                }
              />
            </div>

            {!hasPortalSession && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleLogin}
                disabled={loginLoading || !selectedPlateformId}
              >
                {loginLoading ? 'Connexion…' : 'Se connecter au portail'}
              </button>
            )}

            {hasPortalSession && (
              <div className="d-flex align-items-center gap-2">
                <span className={statusBadgeClass(true)}>Connecté</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleLogin}
                  disabled={loginLoading || !selectedPlateformId}
                >
                  {loginLoading ? 'Actualisation…' : 'Rafraîchir la connexion'}
                </button>
              </div>
            )}
          </div>

          <div className="mt-3 d-flex flex-wrap align-items-center gap-3">
            <div>
              <span className={statusBadgeClass(hasPortalSession)}>
                {hasPortalSession ? 'Session portail disponible' : 'Non connecté'}
              </span>
            </div>
            {loginStatus?.source && (
              <small className="text-muted">
                Source de la session : <strong>{loginStatus.source}</strong>
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <i className="fas fa-tachometer-alt me-2"></i>
            Dashboard
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <i className="fas fa-cogs me-2"></i>
            Gestionnaire de Services
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            <i className="fas fa-star me-2"></i>
            Avis Clients
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'promotions' ? 'active' : ''}`}
            onClick={() => setActiveTab('promotions')}
          >
            <i className="fas fa-tags me-2"></i>
            Promotions
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <i className="fas fa-shopping-cart me-2"></i>
            Commandes
          </button>
        </li>
      </ul>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
      <div className="card">
        <div className="card-body">
          {/* Category Sync Section */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-3">
              <i className="fas fa-folder me-2"></i>
              Gestion des Catégories
            </h6>
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label">Sélectionner un magasin</label>
                <Select
                  options={allStores.map(store => ({
                    value: store._id || store.id,
                    label: `${store.name} (${store.storeCode})`,
                    storeCode: store.storeCode
                  }))}
                  value={selectedStoreForCategorySync}
                  onChange={(selected) => setSelectedStoreForCategorySync(selected)}
                  placeholder="Choisir un magasin..."
                  isClearable
                  isSearchable
                  isDisabled={!selectedPlateformId || allStores.length === 0}
                />
                {selectedStoreForCategorySync && (() => {
                  const store = allStores.find(s => (s._id || s.id) === selectedStoreForCategorySync.value);
                  return store ? (
                    <small className="text-muted">
                      Store Code (VendorId): {store.storeCode}
                    </small>
                  ) : null;
                })()}
              </div>
              <div className="col-md-6">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSyncCategoriesPortal}
                  disabled={!selectedPlateformId || !selectedStoreForCategorySync}
                >
                  <i className="fas fa-sync me-2"></i>
                  Synchroniser les catégories
                </button>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="d-flex flex-wrap align-items-center mb-3 gap-3">
            <div>
              <h5 className="mb-0">Disponibilités par store</h5>
              <small className="text-muted">
                Rafraîchissez pour récupérer l'état actuel des restaurants liés à cette plateforme.
              </small>
            </div>
            <div className="ms-auto d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleTestConnection}
                disabled={!selectedPlateformId}
              >
                Test Connexion
              </button>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleSyncReviews}
                disabled={!selectedPlateformId}
              >
                Sync Avis
              </button>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleSyncProducts}
                disabled={!selectedPlateformId}
              >
                Sync Produits
              </button>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleSyncOrders}
                disabled={!selectedPlateformId}
              >
                Sync Commandes
              </button>
              <button
                type="button"
                className="btn btn-outline-warning btn-sm"
                onClick={handleSyncOrdersHistory}
                disabled={!selectedPlateformId}
              >
                Historique Commandes
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleSyncAddons}
                disabled={!selectedPlateformId}
              >
                Sync Addons
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleFetchAvailabilities}
                disabled={availabilityLoading || !selectedPlateformId}
              >
                {availabilityLoading ? 'Chargement…' : 'Disponibilités'}
              </button>
            </div>
          </div>

          {(statusLoading || statusPayload) && (
            <div className="mb-3">
              <h6 className="fw-semibold">Statut vendeur</h6>
              {statusLoading && <div className="text-center py-2">Chargement du statut…</div>}
              {!statusLoading && statusPayload && (
                <pre className="bg-light p-3 rounded small mb-0">
                  {JSON.stringify(statusPayload, null, 2)}
                </pre>
              )}
            </div>
          )}

          {(reviewsLoading || reviewsPayload) && (
            <div className="mb-3">
              <h6 className="fw-semibold">Avis récents</h6>
              {reviewsLoading && <div className="text-center py-2">Chargement des avis…</div>}
              {!reviewsLoading && reviewsPayload && (
                <pre className="bg-light p-3 rounded small mb-0">
                  {JSON.stringify(reviewsPayload, null, 2)}
                </pre>
              )}
            </div>
          )}

          {availabilityLoading && (
            <div className="text-center py-3">Chargement des disponibilités…</div>
          )}

          {!availabilityLoading && availabilities.length === 0 && (
            <div className="alert alert-info" role="alert">
              Aucune disponibilité à afficher. Sélectionnez une plateforme Glovo puis cliquez sur "Disponibilités".
            </div>
          )}

          {!availabilityLoading && availabilities.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th scope="col">Restaurant</th>
                    <th scope="col">État</th>
                    <th scope="col">Modifiable</th>
                    <th scope="col">Prochaine ouverture</th>
                    <th scope="col">Clôture actuelle</th>
                    <th scope="col">Global Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {availabilities.map((item, index) => (
                    <tr key={`${item.platformRestaurantId || index}-${index}`}>
                      <td>
                        <div className="fw-semibold">{item.name || item.platformRestaurantId || 'Restaurant'}</div>
                        <div className="text-muted small">{item.platformKey || ''}</div>
                      </td>
                      <td>{formatAvailabilityLabel(item.availabilityState)}</td>
                      <td>{item.changeable ? 'Oui' : 'Non'}</td>
                      <td>
                        {item.closedUntil
                          ? new Date(item.closedUntil).toLocaleString()
                          : item.currentSlotEndAt
                          ? new Date(item.currentSlotEndAt).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        {item.checkoutAt
                          ? new Date(item.checkoutAt).toLocaleString()
                          : item.closedReason || '—'}
                      </td>
                      <td>{item.globalEntityId || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && selectedPlateformId && selectedPlateform && (
        <div className="card">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              <i className="fas fa-cogs me-2"></i>
              Gestionnaire de Services Glovo Portal
            </h5>
          </div>
          <div className="card-body p-0">
            <GlovoServiceManager
              plateformId={selectedPlateformId}
              selectedPlateform={selectedPlateform}
            />
          </div>
        </div>
      )}

      {activeTab === 'services' && !selectedPlateformId && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Veuillez sélectionner une plateforme Glovo pour accéder aux services
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
      <div className="card">
        <div className="card-header bg-light">
          <h5 className="mb-0">
            <i className="fas fa-star me-2"></i>
            Avis Clients Glovo
          </h5>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label">Magasins</label>
              <Select
                isMulti
                options={allStores.map(store => ({
                  value: store._id || store.id,
                  label: store.name,
                  storeCode: store.storeCode
                }))}
                value={allStores
                  .map(store => ({
                    value: store._id || store.id,
                    label: store.name,
                    storeCode: store.storeCode
                  }))
                  .filter(opt => selectedStoresForReviews.some(s => s.value === opt.value))}
                onChange={(selected) => setSelectedStoresForReviews(selected || [])}
                placeholder="Tous les magasins..."
                isClearable
                isSearchable
                isDisabled={!selectedPlateformId || allStores.length === 0}
              />
              <small className="text-muted">
                Laissez vide pour tous les magasins
              </small>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date de début</label>
              <input
                type="date"
                className="form-control"
                value={reviewsFromDate}
                onChange={(e) => setReviewsFromDate(e.target.value)}
                disabled={!selectedPlateformId}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Date de fin</label>
              <input
                type="date"
                className="form-control"
                value={reviewsToDate}
                onChange={(e) => setReviewsToDate(e.target.value)}
                disabled={!selectedPlateformId}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary w-100"
                onClick={handleFetchReviewsSection}
                disabled={reviewsFetching || !selectedPlateformId}
              >
                {reviewsFetching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download me-2"></i>
                    Charger
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Statistics */}
          {reviewsStats && (
            <div className="row mb-3">
              <div className="col-md-2">
                <div className="card bg-primary text-white">
                  <div className="card-body p-2">
                    <h6 className="mb-1">Total</h6>
                    <h3 className="mb-0">{reviewsStats.total}</h3>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-warning text-dark">
                  <div className="card-body p-2">
                    <h6 className="mb-1">Moyenne</h6>
                    <h3 className="mb-0">
                      {reviewsStats.avgRating} <i className="fas fa-star"></i>
                    </h3>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-info text-white">
                  <div className="card-body p-2">
                    <h6 className="mb-1">Avec texte</h6>
                    <h3 className="mb-0">{reviewsStats.withText}</h3>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body p-2">
                    <h6 className="mb-2">Distribution</h6>
                    <div className="d-flex justify-content-around">
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 5</div>
                        <strong>{reviewsStats.fiveStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 4</div>
                        <strong>{reviewsStats.fourStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 3</div>
                        <strong>{reviewsStats.threeStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 2</div>
                        <strong>{reviewsStats.twoStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 1</div>
                        <strong>{reviewsStats.oneStar}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reviews Table */}
          {reviewsData.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover table-striped">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th>
                    <th>Note</th>
                    <th>Client</th>
                    <th>Magasin</th>
                    <th>Commentaire</th>
                    <th>Commande</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewsData.map((review, index) => (
                    <tr key={index}>
                      <td>
                        <small>
                          {new Date(review.createdAt || review.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <strong className="me-2">{review.rating}</strong>
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`fas fa-star ${
                                i < review.rating ? 'text-warning' : 'text-muted'
                              }`}
                              style={{ fontSize: '0.8rem' }}
                            ></i>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{review.customerName || 'Anonyme'}</strong>
                        </div>
                        {review.customerEmail && (
                          <small className="text-muted">{review.customerEmail}</small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {review.storeCode || review.globalVendorCode || '-'}
                        </small>
                      </td>
                      <td>
                        {review.text ? (
                          <div style={{ maxWidth: '300px' }}>
                            {review.text}
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">Pas de commentaire</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {review.orderId || '-'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !reviewsFetching ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              {selectedPlateformId 
                ? 'Cliquez sur "Charger" pour récupérer les avis' 
                : 'Sélectionnez une plateforme pour commencer'}
            </div>
          ) : null}
        </div>
      </div>
      )}

      {/* Promotions Tab */}
      {activeTab === 'promotions' && selectedPlateformId && selectedPlateform && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-tags me-2"></i>
              Synchronisation des Promotions
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Synchronisez les promotions depuis Glovo Portal vers votre base de données locale (max 6 mois en arrière).
            </div>

            {/* Store Selection */}
            <div className="mb-3">
              <label className="form-label">
                <i className="fas fa-store me-2"></i>
                Sélectionnez les stores
              </label>
              <Select
                isMulti
                options={allStores.map(store => ({
                  value: store._id || store.id,
                  label: store.name,
                  storeCode: store.storeCode,
                  rawId: store._id || store.id
                }))}
                value={selectedStoresForPromotions}
                onChange={setSelectedStoresForPromotions}
                placeholder="Choisir des stores..."
                className="basic-multi-select"
                classNamePrefix="select"
                isClearable
                isSearchable
                isDisabled={!selectedPlateformId || allStores.length === 0}
              />
              <small className="text-muted">
                Laissez vide pour synchroniser tous les stores de cette plateforme ({allStores.length} store(s) disponible(s))
              </small>
            </div>

            {/* Date Selection */}
            <div className="mb-3">
              <label className="form-label">
                <i className="fas fa-calendar me-2"></i>
                Date de début
              </label>
              <input
                type="date"
                className="form-control"
                value={promotionsStartDate}
                onChange={(e) => setPromotionsStartDate(e.target.value)}
                min={(() => {
                  const sixMonthsAgo = new Date();
                  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                  return sixMonthsAgo.toISOString().split('T')[0];
                })()}
                max={new Date().toISOString().split('T')[0]}
                required
              />
              <small className="text-muted">
                Sélectionnez la date de début (maximum 6 mois en arrière)
              </small>
            </div>

            <button
              className="btn btn-primary"
              onClick={async () => {
                setPromotionsSyncing(true);
                setPromotionsError(null);
                setPromotionsResult(null);
                
                try {
                  // Get vendor codes from selected stores or all platform stores
                  let vendorCodes = [];
                  
                  if (selectedStoresForPromotions.length > 0) {
                    vendorCodes = selectedStoresForPromotions
                      .map(store => store.storeCode)
                      .filter(Boolean);
                  } else {
                    // Use all stores from platform
                    vendorCodes = allStores
                      .map(store => store.storeCode)
                      .filter(Boolean);
                  }

                  if (vendorCodes.length === 0) {
                    setPromotionsError('Aucun store avec un storeCode trouvé');
                    setPromotionsSyncing(false);
                    return;
                  }

                  // Validate start date
                  if (!promotionsStartDate) {
                    setPromotionsError('Veuillez sélectionner une date de début');
                    setPromotionsSyncing(false);
                    return;
                  }

                  // Calculate end date: 6 months from start date or today (whichever is earlier)
                  const startDateObj = new Date(promotionsStartDate);
                  const sixMonthsLater = new Date(startDateObj);
                  sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
                  const today = new Date();
                  const endDateObj = sixMonthsLater < today ? sixMonthsLater : today;

                  const formatDate = (date) => {
                    if (typeof date === 'string') return date; // Already formatted
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };

                  const payload = {
                    plateformId: selectedPlateformId,
                    storeId: vendorCodes[0], // First vendor code as store reference
                    vendorCodes: vendorCodes, // REQUIRED: Array of vendor codes
                    globalEntityId: 'GV_MA',
                    startDate: promotionsStartDate,
                    endDate: formatDate(endDateObj),
                    pageSize: 500,
                    pageOffset: 0,
                    sortByField: 'startDate',
                    sortOrder: 'desc',
                    includeCofundedDeal: true,
                    includeMembershipDeal: true,
                    includeJokerDeal: false,
                    includePlatformEventDeal: false,
                    includeBoostedDeal: false,
                    includeDeliveryFeeDeal: true,
                  };

                  const { syncPromotions } = await import('../../components/GlobalGlovoService');
                  const result = await syncPromotions(payload);
                  setPromotionsResult(result);
                } catch (err) {
                  console.error('Error syncing promotions:', err);
                  setPromotionsError(err.message || 'Erreur lors de la synchronisation');
                } finally {
                  setPromotionsSyncing(false);
                }
              }}
              disabled={promotionsSyncing}
            >
              {promotionsSyncing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Synchronisation en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-sync me-2"></i>
                  Synchroniser les Promotions
                </>
              )}
            </button>

            {promotionsError && (
              <div className="alert alert-danger mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {promotionsError}
              </div>
            )}

            {promotionsResult && (
              <div className="alert alert-success mt-3">
                <h6>
                  <i className="fas fa-check-circle me-2"></i>
                  Synchronisation réussie
                </h6>
                <ul className="mb-0">
                  <li>Total récupéré: {promotionsResult.stats?.totalFetched || 0}</li>
                  <li>Créées: {promotionsResult.stats?.created || 0}</li>
                  <li>Mises à jour: {promotionsResult.stats?.updated || 0}</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'promotions' && !selectedPlateformId && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Veuillez sélectionner une plateforme pour synchroniser les promotions.
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && selectedPlateformId && selectedPlateform && (
        <div className="card">
          <div className="card-header bg-info text-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <i className="fas fa-shopping-cart me-2"></i>
                Gestion des Commandes
              </div>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="autoSyncToggle"
                  checked={isAutoSyncEnabled}
                  onChange={(e) => setIsAutoSyncEnabled(e.target.checked)}
                />
                <label className="form-check-label text-white" htmlFor="autoSyncToggle">
                  Auto-sync (5 min)
                </label>
              </div>
            </div>
            {lastSyncTime && (
              <small className="text-white-50">
                Dernière sync: {lastSyncTime.toLocaleString()}
              </small>
            )}
          </div>
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">Magasin</label>
                <Select
                  options={allStores.map(store => ({
                    value: store._id || store.id,
                    label: `${store.name} (${store.storeCode})`,
                    storeCode: store.storeCode
                  }))}
                  value={selectedStoreForOrders}
                  onChange={(selected) => setSelectedStoreForOrders(selected)}
                  placeholder="Tous les magasins"
                  isClearable
                  isSearchable
                />
                <small className="text-muted">
                  Laisser vide pour synchroniser tous les magasins
                </small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Date/Heure de début</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={ordersTimeFrom}
                  onChange={(e) => setOrdersTimeFrom(e.target.value)}
                  placeholder="Par défaut: maintenant - 30 min"
                />
                <small className="text-muted">
                  Par défaut: maintenant - 30 minutes
                </small>
              </div>
              <div className="col-md-4">
                <label className="form-label">Date/Heure de fin</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={ordersTimeTo}
                  onChange={(e) => setOrdersTimeTo(e.target.value)}
                  placeholder="Par défaut: maintenant"
                />
                <small className="text-muted">
                  Par défaut: maintenant
                </small>
              </div>
            </div>

              

              <button
              className="btn btn-primary"
              onClick={async () => {
                setOrdersSyncLoading(true);
                setOrdersSyncError(null);
                try {
                  const payload = {
                    plateformId: selectedPlateformId,
                    globalEntityId: 'GV_MA',
                  };
                  
                  if (selectedStoreForOrders?.storeCode) {
                    payload.vendorId = selectedStoreForOrders.storeCode;
                  }
                  
                  if (ordersTimeFrom) {
                    payload.timeFrom = new Date(ordersTimeFrom).toISOString();
                  }
                  
                  if (ordersTimeTo) {
                    payload.timeTo = new Date(ordersTimeTo).toISOString();
                  }
                  
                  const response = await syncOrders(payload);
                  setOrdersSyncResult(response);
                  setLastSyncTime(new Date());
                } catch (err) {
                  console.error('Error syncing orders:', err);
                  setOrdersSyncError(err?.response?.data?.error || err.message);
                } finally {
                  setOrdersSyncLoading(false);
                }
              }}
              disabled={ordersSyncLoading}
            >
              {ordersSyncLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Synchronisation...
                </>
              ) : (
                <>
                  <i className="fas fa-sync me-2"></i>
                  Synchroniser les commandes
                </>
              )}
            </button>

            {ordersSyncError && (
              <div className="alert alert-danger mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {ordersSyncError}
              </div>
            )}

            {ordersSyncResult && (
              <div className="mt-4">
                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-primary rounded">
                      <h3 className="mb-0 text-white">
                        {ordersSyncResult.totalOrders || 0}
                      </h3>
                      <small className="text-white">Total commandes</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-success rounded">
                      <h3 className="mb-0 text-white">
                        {ordersSyncResult.updated || 0}
                      </h3>
                      <small className="text-white">Mises à jour</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-warning rounded">
                      <h3 className="mb-0 text-white">
                        {ordersSyncResult.notFound?.length || 0}
                      </h3>
                      <small className="text-white">Non trouvées</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-center p-3 bg-danger rounded">
                      <h3 className="mb-0 text-white">
                        {ordersSyncResult.errors?.length || 0}
                      </h3>
                      <small className="text-white">Erreurs</small>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>Période:</strong> {new Date(ordersSyncResult.timeFrom).toLocaleString()} - {new Date(ordersSyncResult.timeTo).toLocaleString()}
                </div>

                {ordersSyncResult.vendorCodes && ordersSyncResult.vendorCodes.length > 0 && (
                  <div className="mb-3">
                    <strong>Magasins synchronisés:</strong>{' '}
                    {ordersSyncResult.vendorCodes.map((vc, idx) => (
                      <span key={idx} className="badge bg-secondary ms-1">{vc.vendorId}</span>
                    ))}
                  </div>
                )}

                {ordersSyncResult.alreadyGone && ordersSyncResult.alreadyGone.length > 0 && (
                  <div className="alert alert-info">
                    <strong>Déjà passées ({ordersSyncResult.alreadyGone.length}):</strong>{' '}
                    {ordersSyncResult.alreadyGone.map(o => o.orderId).join(', ')}
                  </div>
                )}

                {ordersSyncResult.notFound && ordersSyncResult.notFound.length > 0 && (
                  <div className="alert alert-warning">
                    <strong>Non trouvées ({ordersSyncResult.notFound.length}):</strong>
                    <ul className="mb-0 mt-2">
                      {ordersSyncResult.notFound.map((o, idx) => (
                        <li key={idx}>{o.orderId} - {o.status}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {ordersSyncResult.errors && ordersSyncResult.errors.length > 0 && (
                  <div className="alert alert-danger">
                    <strong>Erreurs:</strong>
                    <ul className="mb-0 mt-2">
                      {ordersSyncResult.errors.map((err, idx) => (
                        <li key={idx}>{err.orderId}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="btn btn-sm btn-outline-secondary mt-2"
                  onClick={() => setOrdersSyncResult(null)}
                >
                  <i className="fas fa-times me-1"></i>
                  Effacer les résultats
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'orders' && !selectedPlateformId && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Veuillez sélectionner une plateforme pour gérer les commandes.
        </div>
      )}

      {activeTab === 'orders' && selectedPlateformId && selectedPlateform && (
        <div className="card mt-4">
          <div className="card-header bg-secondary text-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <i className="fas fa-user-cog me-2"></i>
                Sync User Data
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Limit</label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  max={50}
                  value={syncUserLimit}
                  onChange={(e) => setSyncUserLimit(e.target.value)}
                />
                <small className="text-muted">Par défaut 50 — max 50</small>
              </div>
              <div className="col-md-9 d-flex">
                <button
                  className="btn btn-primary"
                  onClick={() => handleSyncUserData()}
                  disabled={syncUserLoading}
                >
                  {syncUserLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                      Synchronisation…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync me-2"></i>
                      Lancer la synchronisation
                    </>
                  )}
                </button>

                <div className="ms-3">
                  {syncUserError && (
                    <div className="alert alert-danger mb-0">
                      {syncUserError}
                    </div>
                  )}
                  {syncUserResult && (
                    <div className="alert alert-success mb-0">
                      <div>Traités: {syncUserResult.processed || 0} — Mis à jour: {syncUserResult.updated || 0} — Échoués: {syncUserResult.failed || 0}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {syncUserResult && (
              <div className="mt-3">
                <pre className="bg-light p-2 small mb-0">{
                  (() => {
                    try {
                      return JSON.stringify(syncUserResult, null, 2);
                    } catch (e) {
                      try {
                        return String(syncUserResult);
                      } catch (_) {
                        return 'Non-serializable result';
                      }
                    }
                  })()
                }</pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Sync Modal */}
      <CategorySyncModal
        modalId="categorySyncModal"
        isLoading={categorySyncLoading}
        categories={categorySyncData?.categories || []}
        syncStats={categorySyncData}
        error={categorySyncError}
        onCategoryClick={handleCategoryClick}
      />

      {/* Product Sync Modal */}
      <ProductSyncModal
        modalId="productSyncModal"
        isLoading={productSyncLoading}
        products={productSyncData?.products || []}
        syncStats={productSyncData}
        error={productSyncError}
        categoryName={selectedCategory?.name || ''}
      />

      {/* Glovo Modal for displaying API results */}
      <GlovoModal
        modalId="glovoModal"
        title={modalTitle}
        data={modalData}
        loading={modalLoading}
        error={modalError}
        onClose={closeModal}
      />
    </div>
  );
}

export default GlovoPlatformDashboard;
